import { useMutation } from "@tanstack/react-query";
import { GenerateReportFormValues, ReportErrorKey } from "@/types/report.types";
import { reportsApi } from "@/api/reports.api";
import { ApiError } from "@/api/baseApi";
import { HttpStatus } from "@/types/http-status.types";
import { shouldRetry } from "@/providers/QueryProvider";

const isNoReportDataStatus = (status: number) =>
  status === HttpStatus.UNPROCESSABLE_ENTITY || status === HttpStatus.NOT_FOUND;

const isNoReportDataError = (error: unknown): boolean => {
  if (error instanceof ApiError && isNoReportDataStatus(error.status)) {
    return true;
  }

  return error instanceof Error && error.message === ReportErrorKey.NO_TRANSACTIONS_FOUND;
};

export const useGenerateReportMutation = () => {
  return useMutation({
    retry: (failureCount, error) =>
      !isNoReportDataError(error) && shouldRetry(failureCount, error),
    mutationFn: async (formValues: GenerateReportFormValues) => {
      try {
        return await reportsApi.generateReport(formValues);
      } catch (error) {
        // 422 is the current no-data contract; 404 is kept for local/older backends
        if (error instanceof ApiError && isNoReportDataStatus(error.status)) {
          throw new Error(ReportErrorKey.NO_TRANSACTIONS_FOUND);
        }

        if (error instanceof Error) {
          if (error.message.includes("date")) {
            throw new Error(ReportErrorKey.INVALID_DATE_FORMAT);
          }
        }

        throw error;
      }
    },
  });
}; 